import {
  useCallback,
  useRef,
  useEffect,
  useState,
  useMemo,
  FunctionComponent,
} from "react";
import { useAppDispatch, useAppSelector } from "@store/store";
import {
  selectMediaFile,
  setMediaAction,
  setIsPlayingAction,
} from "@store/media/mediaReducer";
import { logError, logInfo } from "@lib/logger";
import Hls from "hls.js";
import { VideoMediaFile } from "@dto/VideoMediaFile";
import { VideoModal } from "./VideoModal";

import "./VideoPlayer.css";

export const VideoPlayer: FunctionComponent = () => {
  const dispatch = useAppDispatch();
  const media = useAppSelector(selectMediaFile);
  const videoRef = useRef<HTMLVideoElement>(null);
  const progressRef = useRef<HTMLDivElement>(null);
  const hlsRef = useRef<Hls | null>(null);
  const [videoDimensions, setVideoDimensions] = useState({
    width: 0,
    height: 0,
  });
  const [optimalDimensions, setOptimalDimensions] = useState({
    width: "100%",
    height: "auto",
  });

  const handleClose = useCallback(() => {
    dispatch(setMediaAction(null));
  }, [dispatch]);

  const isOpen = useMemo(() => {
    return media?.type === "VideoMediaFile" && media.streamUrl;
  }, [media]);

  useEffect(() => {
    if (
      !media ||
      media.type !== "VideoMediaFile" ||
      !videoRef.current ||
      !media.streamUrl
    ) {
      return;
    }

    const videoElement = videoRef.current;
    const videoSrc = (media as VideoMediaFile)?.streamUrl;

    if (hlsRef.current) {
      hlsRef.current.destroy();
      hlsRef.current = null;
    }

    const isHLS = videoSrc.toLowerCase().includes(".m3u8");

    if (isHLS) {
      if (videoElement.canPlayType("application/vnd.apple.mpegurl")) {
        videoElement.src = videoSrc;
      } else if (Hls.isSupported()) {
        const hls = new Hls({
          maxBufferLength: 30,
          maxMaxBufferLength: 600,
        });

        hls.on(Hls.Events.ERROR, (event, data) => {
          if (data.fatal) {
            switch (data.type) {
              case Hls.ErrorTypes.NETWORK_ERROR:
                logInfo("Fatal network error, trying to recover");
                hls.startLoad();
                break;
              case Hls.ErrorTypes.MEDIA_ERROR:
                logInfo("Fatal media error, trying to recover");
                hls.recoverMediaError();
                break;
              default:
                logError("Fatal HLS error, cannot recover");
                break;
            }
          }
        });

        hls.loadSource(videoSrc);
        hls.attachMedia(videoElement);
        hlsRef.current = hls;
      } else {
        logError("HLS is not supported in this browser");
        videoElement.src = videoSrc;
      }
    } else {
      logInfo("Using regular video playback");
      videoElement.src = videoSrc;
    }

    const handlePlay = () => {
      dispatch(setIsPlayingAction(true));
    };

    const handlePause = () => {
      dispatch(setIsPlayingAction(false));
    };

    const handleEnded = () => {
      dispatch(setIsPlayingAction(false));
      if (progressRef.current) {
        progressRef.current.style.width = "0%";
      }
    };

    const updateProgress = () => {
      if (
        videoElement &&
        progressRef.current &&
        !isNaN(videoElement.duration)
      ) {
        const percentage =
          (videoElement.currentTime / videoElement.duration) * 100;
        progressRef.current.style.width = `${percentage}%`;
      }
      requestAnimationFrame(updateProgress);
    };

    const handleMetadataLoaded = () => {
      if (!videoRef.current) return;

      const videoWidth = videoRef.current.videoWidth;
      const videoHeight = videoRef.current.videoHeight;
      setVideoDimensions({ width: videoWidth, height: videoHeight });

      calculateOptimalDimensions(videoWidth, videoHeight);
    };

    videoElement.addEventListener("play", handlePlay);
    videoElement.addEventListener("pause", handlePause);
    videoElement.addEventListener("ended", handleEnded);
    videoElement.addEventListener("loadedmetadata", handleMetadataLoaded);

    const animationId = requestAnimationFrame(updateProgress);

    return () => {
      videoElement.removeEventListener("play", handlePlay);
      videoElement.removeEventListener("pause", handlePause);
      videoElement.removeEventListener("ended", handleEnded);
      videoElement.removeEventListener("loadedmetadata", handleMetadataLoaded);
      cancelAnimationFrame(animationId);

      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }
    };
  }, [media, dispatch]);

  useEffect(() => {
    const handleResize = () => {
      if (videoDimensions.width && videoDimensions.height) {
        calculateOptimalDimensions(
          videoDimensions.width,
          videoDimensions.height,
        );
      }
    };

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [videoDimensions]);

  const calculateOptimalDimensions = (
    videoWidth: number,
    videoHeight: number,
  ) => {
    if (!videoWidth || !videoHeight) {
      return;
    }

    const viewportWidth = window.innerWidth * 0.8;
    const viewportHeight = window.innerHeight * 0.8;

    const videoAspectRatio = videoWidth / videoHeight;
    const screenAspectRatio = viewportWidth / viewportHeight;

    let width: number, height: number;

    if (videoAspectRatio > screenAspectRatio) {
      width = Math.min(viewportWidth, videoWidth);
      height = width / videoAspectRatio;
    } else {
      height = Math.min(viewportHeight, videoHeight);
      width = height * videoAspectRatio;
    }

    setOptimalDimensions({
      width: `${Math.round(width)}px`,
      height: `${Math.round(height)}px`,
    });
  };

  return (
    <>
      <VideoModal
        isOpen={isOpen}
        onClose={handleClose}
        width={optimalDimensions.width}
        height={optimalDimensions.height}>
        <video
          ref={videoRef}
          controls={true}
          controlsList="noplaybackrate nopictureInpicture"
          autoPlay
          disablePictureInPicture
        />
      </VideoModal>
    </>
  );
};
